import { useState } from "react";
import { useAuth } from "../components/AuthContext";
import { useLiveData, useActionWithRefresh } from "../hooks/useLiveData";
import {
  getAllUsers,
  createUser,
  updateUserRole,
  updateUserPassword,
  updateUserCredentials,
  deleteUser,
  resetUserPassword,
  sendTempPasswordEmail,
  type PortalUser,
} from "../services/auth";
import type { PortalRole } from "../types";

const roleColors: Record<PortalRole, string> = {
  admin: "#ef4444",
  authorizer: "#f59e0b",
  user: "#3b82f6",
};

const roleDescriptions: Record<PortalRole, string> = {
  admin: "Full access — pipeline, gates, users, everything",
  authorizer: "Approve deploys, rollbacks, prod promotions",
  user: "Create versions, manage tickets, promote to Dev/Beta",
};

export default function Users() {
  const { can, session } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editField, setEditField] = useState<"role" | "password" | "credentials" | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Form states
  const [createForm, setCreateForm] = useState({
    username: "",
    password: "",
    displayName: "",
    email: "",
    role: "user" as PortalRole,
    sendEmail: true,
  });
  const [editRole, setEditRole] = useState<PortalRole>("user");
  const [editPassword, setEditPassword] = useState("");
  const [editCreds, setEditCreds] = useState({ accessKeyId: "", secretAccessKey: "" });
  const [createProgress, setCreateProgress] = useState<{ step: string; status: "pending" | "active" | "done" | "error" }[]>([]);

  const users = useLiveData<Omit<PortalUser, "passwordHash" | "secretAccessKey">[]>({
    fetcher: getAllUsers,
    interval: 30000,
  });

  const { busy, actionError, execute, clearError } = useActionWithRefresh(users.refresh);

  if (!can("admin:users")) {
    return (
      <div className="page">
        <div className="page-header">
          <h2>User Management</h2>
          <p>You don't have permission to manage users.</p>
        </div>
      </div>
    );
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateProgress([]);

    const progressHandler = (step: string, status: "pending" | "active" | "done" | "error") => {
      setCreateProgress((prev) => {
        const existing = prev.findIndex((p) => p.step === step);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = { step, status };
          return updated;
        }
        return [...prev, { step, status }];
      });
    };

    try {
      await createUser(createForm, progressHandler);
      await users.refresh();
      setTimeout(() => {
        setCreateForm({ username: "", password: "", displayName: "", email: "", role: "user", sendEmail: true });
        setShowCreateForm(false);
        setCreateProgress([]);
      }, 1500);
    } catch (err: any) {
      clearError();
    }
  }

  async function handleUpdateRole(username: string) {
    await execute(`role-${username}`, () => updateUserRole(username, editRole));
    setEditingUser(null);
    setEditField(null);
  }

  async function handleUpdatePassword(username: string) {
    await execute(`password-${username}`, () => updateUserPassword(username, editPassword));
    setEditingUser(null);
    setEditField(null);
    setEditPassword("");
  }

  async function handleUpdateCredentials(username: string) {
    await execute(`creds-${username}`, () => updateUserCredentials(username, editCreds.accessKeyId, editCreds.secretAccessKey));
    setEditingUser(null);
    setEditField(null);
    setEditCreds({ accessKeyId: "", secretAccessKey: "" });
  }

  async function handleResetPassword(username: string, email: string) {
    await execute(`reset-${username}`, async () => {
      const tempPassword = await resetUserPassword(username);
      if (email) {
        try {
          await sendTempPasswordEmail(email, username, tempPassword);
        } catch {
          // If email fails, show the temp password in an alert as fallback
          alert(`Email failed. Temp password for ${username}: ${tempPassword}`);
        }
      } else {
        alert(`No email on file. Temp password for ${username}: ${tempPassword}`);
      }
    });
  }

  async function handleDelete(username: string) {
    await execute(`delete-${username}`, () => deleteUser(username));
    setConfirmDelete(null);
  }

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2>User Management</h2>
            <p>Manage portal users, roles, and credentials.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowCreateForm(!showCreateForm)}>
            + New User
          </button>
        </div>
      </div>

      {actionError && (
        <div className="error-banner">
          ⚠️ {actionError}
          <button onClick={clearError} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer" }}>✕</button>
        </div>
      )}

      {/* Create user form */}
      {showCreateForm && (
        <div className="card animate-slide-down" style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ marginBottom: "1rem" }}>Create New User</h3>
          <form onSubmit={handleCreate}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={createForm.displayName}
                  onChange={(e) => setCreateForm({ ...createForm, displayName: e.target.value })}
                  placeholder="John Smith"
                  required
                />
              </div>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={createForm.username}
                  onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                  placeholder="jsmith"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder="john@company.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as PortalRole })}
                  style={{ width: "100%", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--border)" }}
                >
                  <option value="user">Developer</option>
                  <option value="authorizer">Authorizer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label>Password (leave blank for auto temp password)</label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  placeholder="Auto-generated if empty"
                />
              </div>
              <div className="form-group" style={{ display: "flex", alignItems: "flex-end" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={createForm.sendEmail}
                    onChange={(e) => setCreateForm({ ...createForm, sendEmail: e.target.checked })}
                  />
                  Send credentials via email
                </label>
              </div>
            </div>

            <div className="form-group" style={{ marginTop: "0.5rem" }}>
              <p style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                IAM user and access keys are created automatically. If no password is set, a temp password is generated and the user must change it on first login.
              </p>
            </div>

            <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
              <button type="submit" className="btn btn-primary" disabled={createProgress.length > 0}>
                {createProgress.length > 0 ? "Creating..." : "Create User"}
              </button>
              <button type="button" className="btn" onClick={() => { setShowCreateForm(false); setCreateProgress([]); }} disabled={createProgress.length > 0 && !createProgress.some((p) => p.status === "error")}>
                Cancel
              </button>
            </div>

            {/* Progress stepper */}
            {createProgress.length > 0 && (
              <div className="create-progress animate-fade-in" style={{ marginTop: "1rem" }}>
                {createProgress.map((p) => (
                  <div key={p.step} className={`progress-step ${p.status}`}>
                    <span className="progress-step-icon">
                      {p.status === "done" ? "✓" : p.status === "active" ? "⟳" : p.status === "error" ? "✕" : "○"}
                    </span>
                    <span className="progress-step-label">{p.step}</span>
                  </div>
                ))}
              </div>
            )}
          </form>
        </div>
      )}

      {/* Role legend */}
      <div className="card" style={{ marginBottom: "1.5rem", padding: "1rem" }}>
        <div style={{ display: "flex", gap: "2rem", fontSize: "0.8rem" }}>
          {(["admin", "authorizer", "user"] as PortalRole[]).map((role) => (
            <div key={role} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span className="role-pill" style={{ backgroundColor: roleColors[role] }}>{role}</span>
              <span style={{ color: "var(--text-muted)" }}>{roleDescriptions[role]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* User list */}
      <div className="card">
        <table style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Access Key</th>
              <th>Created</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(users.data || []).map((user) => (
              <tr key={user.username} className="animate-fade-in">
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <div className="user-avatar">{user.displayName.charAt(0).toUpperCase()}</div>
                    <div>
                      <strong>{user.displayName}</strong>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>@{user.username}</div>
                    </div>
                    {user.username === session?.username && (
                      <span className="badge badge-info" style={{ fontSize: "0.6rem" }}>you</span>
                    )}
                  </div>
                </td>
                <td>
                  {editingUser === user.username && editField === "role" ? (
                    <div style={{ display: "flex", gap: "0.35rem", alignItems: "center" }}>
                      <select
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value as PortalRole)}
                        style={{ padding: "0.25rem", borderRadius: "4px", border: "1px solid var(--border)", fontSize: "0.8rem" }}
                      >
                        <option value="user">Developer</option>
                        <option value="authorizer">Authorizer</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button className="mini-btn promote" onClick={() => handleUpdateRole(user.username)}>✓</button>
                      <button className="mini-btn rollback" onClick={() => { setEditingUser(null); setEditField(null); }}>✕</button>
                    </div>
                  ) : (
                    <span className="role-pill" style={{ backgroundColor: roleColors[user.role] }}>{user.role}</span>
                  )}
                </td>
                <td>
                  <code style={{ fontSize: "0.75rem" }}>{user.accessKeyId?.substring(0, 8)}...</code>
                </td>
                <td style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                  {new Date(user.createdAt).toLocaleDateString()}
                </td>
                <td style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                  {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : "Never"}
                </td>
                <td>
                  {user.username !== session?.username && (
                    <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                      <button
                        className="mini-btn deploy"
                        onClick={() => { setEditingUser(user.username); setEditField("role"); setEditRole(user.role); }}
                      >
                        Role
                      </button>
                      <button
                        className="mini-btn deploy"
                        onClick={() => { setEditingUser(user.username); setEditField("password"); setEditPassword(""); }}
                      >
                        Password
                      </button>
                      <button
                        className="mini-btn promote"
                        onClick={() => handleResetPassword(user.username, (user as any).email || "")}
                        disabled={busy === `reset-${user.username}`}
                        title="Generate temp password and email it"
                      >
                        {busy === `reset-${user.username}` ? "⟳" : "🔑"} Reset
                      </button>
                      <button
                        className="mini-btn deploy"
                        onClick={() => { setEditingUser(user.username); setEditField("credentials"); setEditCreds({ accessKeyId: "", secretAccessKey: "" }); }}
                      >
                        Keys
                      </button>
                      {confirmDelete === user.username ? (
                        <>
                          <button className="mini-btn rollback" onClick={() => handleDelete(user.username)} disabled={busy === `delete-${user.username}`}>
                            Confirm
                          </button>
                          <button className="mini-btn" onClick={() => setConfirmDelete(null)} style={{ background: "var(--bg)" }}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button className="mini-btn rollback" onClick={() => setConfirmDelete(user.username)}>
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {(users.data || []).length === 0 && (
          <p style={{ padding: "1.5rem", color: "var(--text-muted)", textAlign: "center" }}>No users found.</p>
        )}
      </div>

      {/* Edit modals (inline below table) */}
      {editingUser && editField === "password" && (
        <div className="card animate-slide-down" style={{ marginTop: "1rem" }}>
          <h4>Change password for @{editingUser}</h4>
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", alignItems: "center" }}>
            <input
              type="password"
              value={editPassword}
              onChange={(e) => setEditPassword(e.target.value)}
              placeholder="New password"
              style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--border)", flex: 1 }}
            />
            <button className="btn btn-primary" onClick={() => handleUpdatePassword(editingUser)} disabled={!editPassword || busy !== null}>
              Update
            </button>
            <button className="btn" onClick={() => { setEditingUser(null); setEditField(null); }}>Cancel</button>
          </div>
        </div>
      )}

      {editingUser && editField === "credentials" && (
        <div className="card animate-slide-down" style={{ marginTop: "1rem" }}>
          <h4>Update IAM credentials for @{editingUser}</h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginTop: "0.75rem" }}>
            <input
              type="text"
              value={editCreds.accessKeyId}
              onChange={(e) => setEditCreds({ ...editCreds, accessKeyId: e.target.value })}
              placeholder="Access Key ID (AKIA...)"
              style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--border)" }}
            />
            <input
              type="password"
              value={editCreds.secretAccessKey}
              onChange={(e) => setEditCreds({ ...editCreds, secretAccessKey: e.target.value })}
              placeholder="Secret Access Key"
              style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--border)" }}
            />
          </div>
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
            <button className="btn btn-primary" onClick={() => handleUpdateCredentials(editingUser)} disabled={!editCreds.accessKeyId || !editCreds.secretAccessKey || busy !== null}>
              Update Keys
            </button>
            <button className="btn" onClick={() => { setEditingUser(null); setEditField(null); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
